import { BellRing, Plus, SlidersHorizontal } from 'lucide-react';
import { Button, EmptyState, formatNumber, Skeleton } from '@platform/design-system';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { Location, StockLevel } from '../../inventory/inventory.ts';
import { usePageContext } from '../../usePageContext.ts';
import type { OptionIndex } from '../options.ts';
import type { ProductFormValues } from '../product-form.ts';
import { DepotStockCard } from './DepotStockCard.tsx';
import { useFieldError } from './sections.ts';
import {
  isLockedColumn,
  NO_CHOICES,
  depotUnits,
  withoutDepot,
  stockColumnsOf,
  withColumnAdded,
  withColumnDropped,
  type StockColumnChoices,
} from './stock-columns.ts';
import editorStyles from './ProductEditor.module.css';
import { EditorSectionCard } from './EditorSectionCard.tsx';
import styles from './StockSection.module.css';

export type EditorStock = {
  readonly locations: readonly Location[];
  readonly levels: readonly StockLevel[];
  readonly isPending: boolean;
};

export type DepotCreation = (onCreated: (depot: Location) => void) => void;

export type StockSectionProps = {
  stock: EditorStock;
  index: OptionIndex;
  canWriteStock: boolean;
  onAdjustStock: (() => void) | null;
  onCreateDepot: DepotCreation | null;
};

export const hasStockHistory = (levels: readonly StockLevel[], variantId: string | null): boolean =>
  variantId !== null && levels.some((level) => level.variantId === variantId);

type DepotChipsProps = {
  others: readonly Location[];
  onAdd: (location: Location) => void;
  onCreate: (() => void) | null;
};

const DepotChips = ({ others, onAdd, onCreate }: DepotChipsProps) => {
  const { t } = usePageContext();
  return (
    <div className={styles['addDepots']}>
      {others.length === 0 ? null : (
        <span className={styles['hint']}>{t('productEditor.stockAddDepot')}</span>
      )}
      <ul className={styles['chips']} aria-label={t('productEditor.stockDepots')}>
        {others.map((location) => (
          <li key={location.id}>
            <button
              type="button"
              className={styles['chip']}
              onClick={() => {
                onAdd(location);
              }}
            >
              <Plus size={14} aria-hidden="true" />
              {location.name}
            </button>
          </li>
        ))}
        {onCreate === null ? null : (
          <li>
            <button
              type="button"
              className={`${styles['chip'] ?? ''} ${styles['chipCreate'] ?? ''}`}
              onClick={onCreate}
            >
              <Plus size={14} aria-hidden="true" />
              {t('depots.add')}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
};

export const StockSection = ({
  stock,
  index,
  canWriteStock,
  onAdjustStock,
  onCreateDepot,
}: StockSectionProps) => {
  const { t, locale } = usePageContext();
  const { control, getValues, setValue, formState } = useFormContext<ProductFormValues>();
  const errorText = useFieldError();
  const stockError = errorText(formState.errors.stockTotals?.['root'] ?? formState.errors.stockTotals);
  const [rows, stockTotals] = useWatch<ProductFormValues, ['variants', 'stockTotals']>({
    control,
    name: ['variants', 'stockTotals'],
  });
  const [choices, setChoices] = useState<StockColumnChoices>(NO_CHOICES);
  const accepts = (variantId: string | null) => canWriteStock && !hasStockHistory(stock.levels, variantId);
  const offersEntry = rows.some((row) => row.enabled && accepts(row.variantId));
  const columns = stockColumnsOf({
    ...choices,
    locations: stock.locations,
    levels: stock.levels,
    entered: stockTotals,
  });
  const others = stock.locations.filter((location) => !columns.includes(location));

  const totalUnits = columns.reduce(
    (sum, location) =>
      sum +
      depotUnits({
        rows,
        locationId: location.id,
        levels: stock.levels,
        accepts,
        typed: stockTotals[location.id],
      }),
    0,
  );

  const dropColumn = (location: Location) => {
    setValue('stockTotals', withoutDepot(getValues('stockTotals'), location.id), { shouldDirty: true });
    setValue('thresholds', withoutDepot(getValues('thresholds'), location.id), { shouldDirty: true });
    setValue('stockSplits', withoutDepot(getValues('stockSplits'), location.id), { shouldDirty: true });
    setChoices(withColumnDropped(choices, location.id));
  };

  const createDepot =
    onCreateDepot === null || stock.isPending
      ? null
      : () => {
          onCreateDepot((created) => {
            setChoices((current) => withColumnAdded(current, created.id));
          });
        };
  const addable = offersEntry ? others : [];

  const depots = () => {
    if (stock.isPending) return <Skeleton height="96px" label={t('shell.loading')} />;
    if (stock.locations.length === 0)
      return <EmptyState title={t('inventory.noDepots')} description={t('inventory.noDepotsHelp')} />;
    return (
      <>
        {stockError === undefined ? null : (
          <p role="alert" className={editorStyles['fieldError']}>
            {stockError}
          </p>
        )}
        <div className={styles['depots']}>
          {columns.map((location) => (
            <DepotStockCard
              key={location.id}
              location={location}
              levels={stock.levels}
              index={index}
              accepts={accepts}
              onDrop={
                offersEntry && !isLockedColumn(stock.levels, location)
                  ? () => {
                      dropColumn(location);
                    }
                  : null
              }
            />
          ))}
        </div>
        <div className={styles['listFooter']}>
          {offersEntry ? (
            <p className={styles['alertNote']}>
              <BellRing size={14} aria-hidden="true" />
              {t('productEditor.stockThresholdHelp')}
            </p>
          ) : null}
          {columns.length > 1 ? (
            <p className={styles['grandTotal']} role="status">
              {t('productEditor.stockGrandTotal', {
                count: totalUnits,
                total: formatNumber(totalUnits, locale),
                depots: formatNumber(columns.length, locale),
              })}
            </p>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <EditorSectionCard section="stock">
      <div className={editorStyles['stack']}>
        <p className={styles['intro']}>
          {offersEntry ? t('productEditor.initialStockHelp') : t('productEditor.stockHelp')}
        </p>
        {depots()}
        {addable.length === 0 && createDepot === null ? null : (
          <DepotChips
            others={addable}
            onAdd={(location) => {
              setChoices(withColumnAdded(choices, location.id));
            }}
            onCreate={createDepot}
          />
        )}
        {onAdjustStock === null ? null : (
          <Button
            icon={<SlidersHorizontal size={20} aria-hidden="true" />}
            variant="outline"
            className={editorStyles['addVariant']}
            onClick={onAdjustStock}
          >
            {t('adjustStock.title')}
          </Button>
        )}
      </div>
    </EditorSectionCard>
  );
};
